import { updateGroupSize } from '../../action/group';
import { EventTypes, TYPES } from '../../render/constants';
import { Rect } from '../region';

// Minimum selection size (in screen px) to show smart layout UI
const MIN_SMART_LAYOUT_SCREEN_WIDTH = 120;
const MIN_SMART_LAYOUT_SCREEN_HEIGHT = 40;

// Render three quick smart-layout buttons (align-left, center, align-right)
// near the selection bounds. Similar visual language to spacing equalize.
export function drawSmartLayoutButtons(context, stageContext) {
  const selected = stageContext.api.getSelectedNodes();
  const nonFrameSelected = selected?.filter((n) => n.type !== TYPES.frame) || [];
  if (!nonFrameSelected || nonFrameSelected.length < 2) {
    stageContext.interactive.smartLayoutRects = null;
    return;
  }
  const { scale } = stageContext.providers;
  const interactive = stageContext.providers.interactive;

  const bounds = stageContext.api.getSelectedNodesRegion();
  const bx = bounds.x * scale.scale + scale.translateX;
  const by = bounds.y * scale.scale + scale.translateY;
  const br = (bounds.x + bounds.w) * scale.scale + scale.translateX;
  const bb = (bounds.y + bounds.h) * scale.scale + scale.translateY;

  // Small-area guard: if selection is too small on screen, hide UI and disable handlers
  const selScreenW = Math.max(0, br - bx);
  const selScreenH = Math.max(0, bb - by);
  if (selScreenW < MIN_SMART_LAYOUT_SCREEN_WIDTH || selScreenH < MIN_SMART_LAYOUT_SCREEN_HEIGHT) {
    stageContext.interactive.smartLayoutRects = null;
    return;
  }

  const pad = 16;
  const btn = { w: 30, h: 30, gap: 12, margin: 6 };
  const cols = 3;
  const totalW = btn.w * cols + btn.gap * (cols - 1) + btn.margin * 2;
  const panelX = br - totalW - pad;
  const panelY = by + pad; // top-right cluster

  // Draw background panel (elevated, soft rounded) with #8b3eff theme
  const r = 10;
  const panelW = totalW;
  const panelH = btn.h + btn.margin * 2;
  context.save();
  context.shadowColor = 'rgba(0,0,0,0.25)';
  context.shadowBlur = 12;
  context.shadowOffsetX = 0;
  context.shadowOffsetY = 6;
  context.fillStyle = 'rgb(140 63 255 / 60%)';
  context.beginPath();
  context.moveTo(panelX + r, panelY);
  context.arcTo(panelX + panelW, panelY, panelX + panelW, panelY + panelH, r);
  context.arcTo(panelX + panelW, panelY + panelH, panelX, panelY + panelH, r);
  context.arcTo(panelX, panelY + panelH, panelX, panelY, r);
  context.arcTo(panelX, panelY, panelX + panelW, panelY, r);
  context.closePath();
  context.fill();
  // subtle stroke with purple tint
  context.shadowColor = 'transparent';
  context.lineWidth = 1;
  context.strokeStyle = 'rgba(255,255,255,0.15)';
  context.stroke();

  // Buttons (with margin and gap for proper spacing)
  const buttons = [
    { key: 'left', x: panelX + btn.margin, y: panelY + btn.margin },
    { key: 'center', x: panelX + btn.margin + btn.w + btn.gap, y: panelY + btn.margin },
    { key: 'right', x: panelX + btn.margin + (btn.w + btn.gap) * 2, y: panelY + btn.margin },
  ];

  // Which button is hovered
  let hoveredKey = null;
  if (interactive.hoverHandlerId && interactive.hoverHandlerId.startsWith('smart-layout-')) {
    hoveredKey = interactive.hoverHandlerId.replace('smart-layout-', '');
  }

  // icon draw helpers
  const drawIcon = (key, x, y, w, h) => {
    context.save();
    const isHovered = key === hoveredKey;
    context.strokeStyle = isHovered ? '#FFFFFF' : 'rgba(255,255,255,0.9)';
    context.lineWidth = isHovered ? 2.2 : 1.8;
    const px = x + 6,
      py = y + 6,
      pw = w - 12,
      ph = h - 12;
    if (key === 'left') {
      const lens = [pw, pw * 0.7, pw * 0.45];
      for (let i = 0; i < 3; i++) {
        context.beginPath();
        context.moveTo(px, py + i * (ph / 2));
        context.lineTo(px + lens[i], py + i * (ph / 2));
        context.stroke();
      }
    } else if (key === 'center') {
      const cx = x + w / 2;
      context.beginPath();
      context.moveTo(cx, py);
      context.lineTo(cx, py + ph);
      context.stroke();
      const len = pw * 0.35;
      context.beginPath();
      context.moveTo(cx - len / 2, py + ph * 0.25);
      context.lineTo(cx + len / 2, py + ph * 0.25);
      context.moveTo(cx - len / 2, py + ph * 0.75);
      context.lineTo(cx + len / 2, py + ph * 0.75);
      context.stroke();
    } else if (key === 'right') {
      const lens = [pw, pw * 0.7, pw * 0.45];
      for (let i = 0; i < 3; i++) {
        context.beginPath();
        const yline = py + i * (ph / 2);
        context.moveTo(px + pw - lens[i], yline);
        context.lineTo(px + pw, yline);
        context.stroke();
      }
    }
    context.restore();
  };

  // button hit rects in stage coordinates
  const rectsStage = [];
  buttons.forEach((btnMeta, idx) => {
    // hover background for the individual button
    const isHovered = btnMeta.key === hoveredKey;
    if (isHovered) {
      context.save();
      const brR = 8;
      const bx2 = btnMeta.x,
        by2 = btnMeta.y;
      context.fillStyle = 'rgba(255,255,255,0.15)';
      context.beginPath();
      context.moveTo(bx2 + brR, by2);
      context.arcTo(bx2 + btn.w, by2, bx2 + btn.w, by2 + btn.h, brR);
      context.arcTo(bx2 + btn.w, by2 + btn.h, bx2, by2 + btn.h, brR);
      context.arcTo(bx2, by2 + btn.h, bx2, by2, brR);
      context.arcTo(bx2, by2, bx2 + btn.w, by2, brR);
      context.closePath();
      context.fill();
      // focus ring with purple accent
      context.lineWidth = 1.2;
      context.strokeStyle = 'rgba(255,255,255,0.4)';
      context.stroke();
      context.restore();
    }

    drawIcon(btnMeta.key, btnMeta.x, btnMeta.y, btn.w, btn.h);

    // separators between buttons
    if (idx < buttons.length - 1) {
      context.save();
      context.beginPath();
      context.strokeStyle = 'rgba(255,255,255,0.12)';
      context.lineWidth = 1;
      const sx = btnMeta.x + btn.w + btn.gap / 2;
      context.moveTo(sx, panelY + btn.margin + 6);
      context.lineTo(sx, panelY + btn.margin + btn.h - 6);
      context.stroke();
      context.restore();
    }

    const stageX = (btnMeta.x - scale.translateX) / scale.scale;
    const stageY = (btnMeta.y - scale.translateY) / scale.scale;
    rectsStage.push({
      key: btnMeta.key,
      rect: { x: stageX, y: stageY, w: btn.w / scale.scale, h: btn.h / scale.scale },
    });
  });
  context.restore();

  stageContext.interactive.smartLayoutRects = rectsStage;
}

export function getSmartLayoutHandlers(stageContext) {
  const rects = stageContext.interactive.smartLayoutRects;
  if (!rects || rects.length === 0) return [];
  return rects.map(({ key, rect }) => {
    const r = new Rect(rect.x, rect.y, rect.w, rect.h);
    r.id = `smart-layout-${key}`;
    r.type = 'smart-layout';
    r.variant = key; // left | center | right
    return r;
  });
}

export function smartLayoutAction(hitTestContext) {
  const { type, targetHandler, stageContext, provider } = hitTestContext;
  if (!targetHandler || targetHandler.type !== 'smart-layout') return false;

  // Guard: if UI hidden due to small selection, skip interaction
  const { scale } = stageContext.providers;
  const b = stageContext.api.getSelectedNodesRegion();
  const bx = b.x * scale.scale + scale.translateX;
  const by = b.y * scale.scale + scale.translateY;
  const br = (b.x + b.w) * scale.scale + scale.translateX;
  const bb = (b.y + b.h) * scale.scale + scale.translateY;
  const selScreenW = Math.max(0, br - bx);
  const selScreenH = Math.max(0, bb - by);
  if (selScreenW < MIN_SMART_LAYOUT_SCREEN_WIDTH || selScreenH < MIN_SMART_LAYOUT_SCREEN_HEIGHT) {
    return false;
  }

  // Hover: show pointer and highlight
  if (type === EventTypes.move) {
    const interactive = stageContext.providers?.interactive || stageContext.interactive;
    interactive.cursor.set('pointer');
    interactive.hoverHandlerId = targetHandler.id;
    stageContext.api.markNeedRender();
    return true;
  }

  if (type !== EventTypes.down) return false;

  const selectedNodes = stageContext.api.getSelectedNodes().filter((n) => n.type !== TYPES.frame);
  if (!selectedNodes || selectedNodes.length < 2) return false;

  // capture initial boxes for stable delta updates
  const interactive = stageContext.providers?.interactive || stageContext.interactive;
  selectedNodes.forEach((n) => interactive.getMovingNodeInitialBox(n.id));

  const selectedBounds = stageContext.api.getSelectedNodesRegion();
  const variant = targetHandler.variant; // left | center | right

  // Align each node INSIDE the selection bounds (not moving the whole group)
  selectedNodes.forEach((n) => {
    const rect = stageContext.api.getNodeRegion(n).rect;
    let targetX = rect.x;
    if (variant === 'left') {
      targetX = selectedBounds.x;
    } else if (variant === 'right') {
      targetX = selectedBounds.x + selectedBounds.w - rect.w;
    } else if (variant === 'center') {
      targetX = selectedBounds.x + selectedBounds.w / 2 - rect.w / 2;
    }
    const deltaAbs = targetX - rect.x;
    if (Math.abs(deltaAbs) > 0) {
      const cache = interactive.getMovingNodeInitialBox(n.id);
      stageContext.api.updateNode(n.id, { x: cache.x + deltaAbs });
    }
    // text horizontal alignment: set node.textAlign when applicable
    if (n.type === TYPES.text) {
      const align = variant === 'left' ? 'left' : variant === 'right' ? 'right' : 'center';
      if (n.textAlign !== align) {
        stageContext.api.updateNode(n.id, {
          textAlign: align,
          padding: 0,
        });
      }
    }
  });

  // Update potential wrapping groups once after position edits
  const affected = selectedNodes.map((n) => ({ node: stageContext.api.getNode(n.id) }));
  updateGroupSize(stageContext, affected);

  stageContext.api.markNeedRender();
  provider.skipSelectFromUp = true;
  return true;
}
